/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { PrismTheme } from 'prism-react-renderer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inlineCode: {
      backgroundColor: theme.palette.grey[300],
      borderRadius: theme.spacing(0.5),
    },
    codeBlock: {
      position: 'relative',
      fontSize: theme.spacing(1.625),
      letterSpacing: theme.spacing(0),
      lineHeight: theme.spacing(0.2),
      backgroundColor: '#F7F8FB',
      overflowX: 'auto',
    },
    codeBlockHorizontalNumberLine: {
      backgroundColor: '#999999',
      opacity: 0.06,
      width: theme.spacing(5),
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: theme.zIndex.drawer,
    },
    codeBlockLine: {
      position: 'relative',
      zIndex: theme.zIndex.drawer + 4,
      '& span:first-child': {
        display: 'inline-flex',
        justifyContent: 'center',
        textAlign: 'center',
        width: '40px !important',
        userSelect: 'none',
      },
      '& span:nth-child(2)': {
        backgroundColor: '#F7F8FB',
        paddingLeft: theme.spacing(2),
        width: '100%',
      },
    },
    table: {
      margin: '0px',
      '& .MuiTableCell-root': {
        borderLeft: '1px solid rgba(224, 224, 224, 1)',
      },
    },
  })
);

export const BallerinaPrismTheme: PrismTheme = {
  plain: {
    color: '#424242',
  },
  styles: [
    {
      types: ['changed'],
      style: {
        color: 'rgb(162, 191, 252)',
        fontStyle: 'italic',
      },
    },
    {
      types: ['deleted'],
      style: {
        color: 'rgba(239, 83, 80, 0.56)',
        fontStyle: 'italic',
      },
    },
    {
      types: ['inserted', 'attr-name'],
      style: {
        color: 'rgb(72, 118, 214)',
        fontStyle: 'italic',
      },
    },
    {
      types: ['comment'],
      style: {
        color: 'rgb(152, 159, 177)',
        fontStyle: 'italic',
      },
    },
    {
      types: ['string', 'builtin', 'char', 'constant', 'url'],
      style: {
        color: 'rgb(72, 118, 214)',
      },
    },
    {
      types: ['variable'],
      style: {
        color: 'rgb(201, 103, 101)',
      },
    },
    {
      types: ['number'],
      style: {
        color: 'rgb(170, 9, 130)',
      },
    },
    {
      types: ['punctuation'],
      style: {
        color: 'rgb(153, 76, 195)',
      },
    },
    {
      types: ['function', 'selector', 'doctype'],
      style: {
        color: 'rgb(153, 76, 195)',
        fontStyle: 'italic',
      },
    },
    {
      types: ['class-name'],
      style: {
        color: 'rgb(17, 17, 17)',
      },
    },
    {
      types: ['tag'],
      style: {
        color: 'rgb(153, 76, 195)',
      },
    },
    {
      types: ['operator', 'property', 'keyword', 'namespace'],
      style: {
        color: 'rgb(12, 150, 155)',
      },
    },
    {
      types: ['boolean'],
      style: {
        color: 'rgb(188, 84, 84)',
      },
    },
  ],
};

export default useStyles;
